// The round Messages link in the top bar: a message icon and, when something is
// unread, how much.
//
// It is one component, placed by one line in the shell (layouts/DashboardLayout), so
// it can be moved, for instance behind the profile button, by moving that line. The
// shell hands it the selected project and who is signed in; it reads no store itself.
//
// Who sees it is decided by the server, not here. The link is drawn only after the
// unread count has been answered for this project, which the server does for a super
// admin in every project, and for anyone else only when their role has Direct Messages
// or Group Chats ticked there (Settings > Access Management > Mobile Access). So there is
// no second switch: no menu row and no setting of its own.
//
// A REFUSAL (the server says who this is cannot be named, or Messages is not on for them
// here, or the answer is not a count) before the link has been drawn for this project
// and sign-in, which in practice is the first ask, draws nothing and is not asked again
// until the project or the sign-in changes. The server cannot always name the caller,
// and it checks again with 1Office a minute after it could not; a link that kept asking
// from every open page would turn one slow moment there into a steady stream of checks.
//
// A FAILURE (no answer, or a server error, as during a deploy) is different: nothing was
// refused. The link keeps what it showed and asks again on its usual clock, giving up
// only after three in a row, so one bad minute does not take Messages away until the
// page is reloaded.
//
// A refusal AFTER the link has been drawn for this project and sign-in is counted as a
// failure, not taken as final. The server answers the same plain 404 when its own read
// of the person's permissions fails (a database stall), which refuses nothing, and the
// link cannot tell the two apart. So one such answer takes nothing away, and a tick that
// really was removed takes the link away after three asks in a row, about three minutes.
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MessageSquare } from 'lucide-react';
import { messagesAPI } from '../../services/messagesApi';
import { MESSAGES_PATH, MESSAGES_READ_EVENT } from './messagesPath';
import { unreadLabel } from './messageText';
import { tabInView } from './useInViewPoll';

interface MessagesLinkProps {
  /** The project selected in the top bar. Messages are per project. None: nothing is asked. */
  projectId: number | null | undefined;
  /** Who is signed in, so a count read for one person is never shown to the next. */
  userKey?: string | null;
}

// About once a minute while the tab is in view. The page itself keeps closer time.
const ASK_EVERY_MS = 60_000;
// Failures in a row (and refusals, once the link has been drawn) before the link gives up.
const FAILURES_BEFORE_STOPPING = 3;

/** A refusal says no on purpose. Anything else that went wrong is a failure. */
const isRefusal = (err: unknown): boolean => {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 401 || status === 403 || status === 404;
};

/** The count in an answer, or null when the answer is not one. */
const readTotal = (body: unknown): number | null => {
  const answer = body as { success?: unknown; data?: { total?: unknown } | null } | null;
  const total = answer?.data?.total;
  if (answer?.success !== true || typeof total !== 'number' || !Number.isFinite(total) || total < 0) return null;
  return Math.floor(total);
};

export default function MessagesLink({ projectId, userKey }: MessagesLinkProps): ReactElement | null {
  const { pathname } = useLocation();
  // Everything the count depends on. Null asks nothing.
  const key = projectId ? `${userKey ?? ''}\n${projectId}` : null;
  // Kept WITH the key it was asked under (as pages/home/useRead keeps a number): the
  // moment the project changes, the old count stops being shown. A null total is a
  // refusal: nothing is drawn.
  const [held, setHeld] = useState<{ key: string; total: number | null } | null>(null);

  useEffect(() => {
    if (key === null || !projectId) return undefined;
    let current = true;
    let refused = false;
    let asking = false;
    let failures = 0;
    // Has the count been answered for this project and sign-in, so the link was drawn?
    let drawn = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (timer !== null) clearInterval(timer);
      timer = null;
    };
    const refuse = () => {
      refused = true;
      stop();
      setHeld({ key, total: null });
    };
    // An ask that brought no count. A refusal before the link was drawn is final. Anything
    // else leaves what the link showed and lets the clock ask again, three times in a row.
    const missed = (wasRefused: boolean) => {
      failures += 1;
      if ((wasRefused && !drawn) || failures >= FAILURES_BEFORE_STOPPING) refuse();
    };
    // Never while the tab is hidden, never two at once, never again once it has given up.
    const ask = () => {
      if (refused || asking || !tabInView()) return;
      asking = true;
      Promise.resolve()
        .then(() => messagesAPI.unread(projectId))
        .then(
          (res) => {
            asking = false;
            if (!current) return;
            const total = readTotal(res?.data);
            if (total === null) missed(true);
            else {
              failures = 0;
              drawn = true;
              setHeld({ key, total });
            }
          },
          (err: unknown) => {
            asking = false;
            if (!current) return;
            missed(isRefusal(err));
          },
        );
    };
    const start = () => {
      if (timer === null && !refused) timer = setInterval(ask, ASK_EVERY_MS);
    };
    const onViewChange = () => {
      stop();
      if (!tabInView()) return;
      // Back in view: the count may be old, so ask now and start the clock afresh.
      ask();
      start();
    };

    ask();
    if (tabInView()) start();
    document.addEventListener('visibilitychange', onViewChange);
    // The Messages page has just read something: the count on the link is now too high.
    window.addEventListener(MESSAGES_READ_EVENT, ask);
    return () => {
      current = false;
      stop();
      document.removeEventListener('visibilitychange', onViewChange);
      window.removeEventListener(MESSAGES_READ_EVENT, ask);
    };
  }, [key, projectId]);

  const total = key !== null && held?.key === key ? held.total : null;
  if (total === null) return null;

  const here = pathname.replace(/\/+$/, '') === MESSAGES_PATH;
  return (
    <Link
      to={MESSAGES_PATH}
      aria-label={total > 0 ? `Messages, ${total} unread` : 'Messages'}
      aria-current={here ? 'page' : undefined}
      title="Messages"
      // The Search button's round shape and size, so the two sit as a pair. On the
      // narrowest desktop (1024 to 1279px) it sits 8px closer to Search: measured in a
      // browser with seven menus, the bar's usual 16px gap left the menus 2px short of
      // one row at 1024px, and they wrapped to a second.
      className={`relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border bg-white shadow-sm transition-colors hover:border-primary-300 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 lg:-ml-2 xl:ml-0 ${
        here ? 'border-primary-300 text-primary-600' : 'border-gray-200 text-gray-500'
      }`}
    >
      <MessageSquare className="h-4 w-4" aria-hidden="true" />
      {total > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-600 px-1 text-[10px] font-bold leading-none text-white ring-2 ring-white"
        >
          {unreadLabel(total)}
        </span>
      )}
    </Link>
  );
}
